import React,{useEffect,useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PostArtwork from './PostArtwork';
import {
  formatMetric,
  getEngagementScore
} from '../data/mockEngagement';
import * as api from '../lib/api.js';
import './Learnings.css';

const {
  FiArrowUpRight,
  FiBarChart2,
  FiBookOpen,
  FiCheckCircle,
  FiHeart,
  FiMessageCircle,
  FiMessageSquare,
  FiTrendingUp
}=FiIcons;

const workingTags=[
  'Data-driven hook',
  'Client story angle',
  'Clear perspective',
  'Founder insight'
];

const emptyEngagement={
  likes:0,
  comments:0,
  reach:0
};

function getWorkingTag(post,index) {
  if (post.learningTag) {
    return post.learningTag;
  }

  return workingTags[index%workingTags.length];
}

function getRejectionTheme(reason='') {
  const normalized=reason.toLowerCase();

  if (
    normalized.includes('sales')
    || normalized.includes('promot')
    || normalized.includes('pitch')
  ) {
    return 'Tone';
  }

  if (
    normalized.includes('optim')
    || normalized.includes('positive')
    || normalized.includes('hope')
  ) {
    return 'Voice';
  }

  if (
    normalized.includes('headline')
    || normalized.includes('hook')
    || normalized.includes('opening')
  ) {
    return 'Hook';
  }

  return 'Refinement';
}

function LearningPost({post,brand,tag,onOpen,rejected=false}) {
  return (
    <motion.article
      className="learning-card"
      initial={{opacity:0,y:12}}
      animate={{opacity:1,y:0}}
    >
      <button className="learning-thumbnail" onClick={()=> onOpen(post)}>
        <PostArtwork post={post} brand={brand} compact />
      </button>
      <div className="learning-card-body">
        <div className="learning-card-label">
          <span className={`learning-tag ${rejected ? 'avoid-tag' : ''}`}>
            {tag}
          </span>
          <button
            onClick={()=> onOpen(post)}
            aria-label={`Open ${post.headline}`}
          >
            <SafeIcon icon={FiArrowUpRight} />
          </button>
        </div>
        <h3>{post.headline}</h3>
        {rejected && (
          <blockquote>
            <SafeIcon icon={FiMessageCircle} />
            <span>{post.rejectionReason}</span>
          </blockquote>
        )}
      </div>
    </motion.article>
  );
}

function PerformancePost({post,brand,tag,onOpen,rank}) {
  const engagement=post.engagement || emptyEngagement;

  return (
    <motion.article
      className="learning-card performance-card"
      initial={{opacity:0,y:12}}
      animate={{opacity:1,y:0}}
    >
      <button className="learning-thumbnail" onClick={()=> onOpen(post)}>
        <PostArtwork post={post} brand={brand} compact />
        <span className="performance-rank">#{rank}</span>
      </button>
      <div className="learning-card-body">
        <div className="learning-card-label">
          <span className="learning-tag performance-tag">
            {tag}
          </span>
          <button
            onClick={()=> onOpen(post)}
            aria-label={`Open ${post.headline}`}
          >
            <SafeIcon icon={FiArrowUpRight} />
          </button>
        </div>
        <h3>{post.headline}</h3>
        <div className="performance-stats">
          <span>
            <SafeIcon icon={FiHeart} />
            <strong>{formatMetric(engagement.likes)}</strong>
            likes
          </span>
          <span>
            <SafeIcon icon={FiMessageSquare} />
            <strong>{formatMetric(engagement.comments)}</strong>
            comments
          </span>
          <span>
            <SafeIcon icon={FiBarChart2} />
            <strong>{formatMetric(engagement.reach)}</strong>
            reach
          </span>
        </div>
      </div>
    </motion.article>
  );
}

function LearningsScreen({
  posts,
  brand,
  loading,
  error,
  onOpen
}) {
  const [engagementByPostId,setEngagementByPostId]=useState({});

  const postedPostIds=useMemo(
    ()=> posts
      .filter((post)=> post.status==='posted')
      .map((post)=> post.id),
    [posts]
  );

  useEffect(()=> {
    if (!postedPostIds.length) {
      setEngagementByPostId({});
      return undefined;
    }

    let cancelled=false;

    api.postEngagement.getEngagementForPosts(postedPostIds).then(({data})=> {
      if (!cancelled) {
        setEngagementByPostId(data || {});
      }
    });

    return ()=> {
      cancelled=true;
    };
  },[postedPostIds.join(',')]);

  const postsWithEngagement=useMemo(()=> posts.map((post)=> ({
    ...post,
    engagement:engagementByPostId[post.id] ?? null
  })),[posts,engagementByPostId]);

  const approvedPosts=postsWithEngagement.filter(
    (post)=> post.status==='approved'
  );
  const rejectedPosts=postsWithEngagement.filter(
    (post)=> post.status==='rejected' && post.rejectionReason
  );
  const postedPosts=postsWithEngagement.filter(
    (post)=> post.status==='posted'
  );
  const topPosts=postedPosts
    .sort((first,second)=> (
      getEngagementScore(second.engagement || emptyEngagement)
      -getEngagementScore(first.engagement || emptyEngagement)
    ))
    .slice(0,5);

  return (
    <main className="page learnings-page">
      <header className="learnings-header">
        <div>
          <span className="eyebrow">{brand.name}</span>
          <h1>Learnings</h1>
          <p>A quiet record of what resonates and what to leave behind.</p>
        </div>
        <div className="learnings-mark">
          <SafeIcon icon={FiBookOpen} />
        </div>
      </header>

      {error && (
        <p className="learnings-error" role="alert">{error}</p>
      )}

      {loading ? (
        <div className="learnings-loading">
          <p>Loading learnings…</p>
        </div>
      ) : (
        <>
          <section className="learning-section performance-section">
            <div className="learning-section-heading">
              <div>
                <span className="section-kicker performance-kicker">
                  <SafeIcon icon={FiTrendingUp} />
                  Audience signals
                </span>
                <h2>Top performing</h2>
              </div>
              <p>
                Published posts ranked by engagement once metrics are available.
              </p>
            </div>

            {topPosts.length ? (
              <div className="learning-grid">
                {topPosts.map((post,index)=> (
                  <PerformancePost
                    key={post.id}
                    post={post}
                    brand={brand}
                    tag={getWorkingTag(post,index)}
                    onOpen={onOpen}
                    rank={index+1}
                  />
                ))}
              </div>
            ) : (
              <div className="learning-empty">
                Performance signals will appear after this brand publishes a
                post and engagement metrics are recorded.
              </div>
            )}
          </section>

          <section className="learning-section">
            <div className="learning-section-heading">
              <div>
                <span className="section-kicker working-kicker">
                  <SafeIcon icon={FiCheckCircle} />
                  Approval patterns
                </span>
                <h2>What's working</h2>
              </div>
              <p>Ideas that made it through your review.</p>
            </div>

            {approvedPosts.length ? (
              <div className="learning-grid">
                {approvedPosts.map((post,index)=> (
                  <LearningPost
                    key={post.id}
                    post={post}
                    brand={brand}
                    tag={getWorkingTag(post,index)}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            ) : (
              <div className="learning-empty">
                Approved posts will appear here as your library grows.
              </div>
            )}
          </section>

          <section className="learning-section avoid-section">
            <div className="learning-section-heading">
              <div>
                <span className="section-kicker avoid-kicker">
                  <SafeIcon icon={FiMessageCircle} />
                  Review notes
                </span>
                <h2>What to avoid</h2>
              </div>
              <p>Rejection notes, kept close for the next draft.</p>
            </div>

            {rejectedPosts.length ? (
              <div className="learning-grid">
                {rejectedPosts.map((post)=> (
                  <LearningPost
                    key={post.id}
                    post={post}
                    brand={brand}
                    tag={getRejectionTheme(post.rejectionReason)}
                    onOpen={onOpen}
                    rejected
                  />
                ))}
              </div>
            ) : (
              <div className="learning-empty">
                Rejection notes will appear here when you leave feedback on a
                post.
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}

export default LearningsScreen;
